import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  SHIPMENT_FILTER_CATEGORIES,
  SHIPMENT_FILTER_FIELD_DEFINITIONS,
} from './shipmentFilterDefinitions';
import { buildShipmentQuickFilters, EMPTY_FILTERS } from './shipmentListFilters';
import { ShipmentListHeader } from './ShipmentListHeader';
import { useShipmentListData } from './useShipmentListData';
import { ActionPermissions, ListType } from '../../../@types';
import { PrintLabelEntityTypes } from '../../../@types/print-label.type';
import { freightOrderByShipment } from '../../../api/freight';
import { getPrintLabel } from '../../../api/printLabel';
import { SHIPMENT_NUMERIC_FILTER_FIELDS } from '../../../api/shipment';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { FilterBar, type FilterState } from '../../../components/molecules/FilterBar';
import { TablePagination, AutoRefresh } from '../../../components/molecules/Pagination';
import {
  ShipmentTable,
  SortModel,
  StructureRowAction,
} from '../../../components/organisms/ShipmentTable';
import { ColumnSelection } from '../../../components/templates/ColumnSelection';
import { PLATFORM_NAME } from '../../../configs/common';
import { ShipmentTypes } from '../../../configs/enums';
import { isUserHasPermission, PermissionInput } from '../../../configs/permissions';
import { isReceivableStatus } from '../../../constants/shipment';
import { TabHandler } from '../../../handlers/TabHandler';
import { useListExport } from '../../../hooks/useListExport';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { mapNumericFilterItemsForWire } from '../../../utils/filterModel';
import { printLabel } from '../../../utils/print-label-utils';
import { toast } from '../../../utils/toast';

const detailPathFor = (id: number, shipmentTypeCode: string): string => {
  if (shipmentTypeCode === ShipmentTypes.Return) {
    return `/shipments/return/${id}`;
  }
  return `/shipments/${id}`;
};

// A shipment is only receivable via this row action when it's a Dispatch/
// Internal Transfer (Add Stock and Return don't model a physical "arriving"
// step — see ShipmentDetailHeader.tsx's canShowReceiveButton, which mirrors this
// same eligibility rule for the shipment detail page's own Receive button)
// and its status is one of the statuses INCOMING_STATUSES considers receivable.
export const canReceiveRow = (
  row: Record<string, unknown>,
  permissions: PermissionInput[] | null | undefined
): boolean =>
  isUserHasPermission(ActionPermissions.Shipment_View_Receive, permissions) &&
  (row.shipmentTypeCode === ShipmentTypes.Dispatch ||
    row.shipmentTypeCode === ShipmentTypes.InternalTransfer) &&
  isReceivableStatus(row.shipmentStatusCode as string | undefined);

const ShipmentListInner: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const permissions = getUserPermissionsQuery.data;

  const {
    data,
    isFetching,
    page,
    pageSize,
    total,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData,
    filterModel,
    statusOptions,
    typeOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    updateColumnSelection,
    reorderColumns,
    refetch,
  } = useShipmentListData();

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const [columnSelectionOpen, setColumnSelectionOpen] = useState(false);

  TabHandler(`List Shipments | ${PLATFORM_NAME}`);

  React.useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const timer = setInterval(() => refetch(), autoRefreshInterval * 1000);
    return () => clearInterval(timer);
  }, [autoRefreshInterval, refetch]);

  // Memoised: both are handed to FilterBar, and an inline value would be a new
  // identity every render — the shape behind the PR #350 refetch loop.
  const quickFilters = useMemo(
    () => buildShipmentQuickFilters({ statusOptions, typeOptions }),
    [statusOptions, typeOptions]
  );
  const fieldConfig = useMemo(
    () => ({
      fieldDefinitions: SHIPMENT_FILTER_FIELD_DEFINITIONS,
      categories: SHIPMENT_FILTER_CATEGORIES,
    }),
    []
  );

  const handleSearch = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
      applyFilters(newFilters);
    },
    [applyFilters]
  );

  const handleSortChange = useCallback(
    (model: SortModel) => {
      setSortModel({ field: model.field, sort: model.direction });
    },
    [setSortModel]
  );

  const { exportAnchor, setExportAnchor, handleExport } = useListExport({
    fileName: ListType.ShipmentList,
    columns,
    columnSelectionData,
    filterModel,
    // Mirror the list query's numeric-column operator fix so an exported
    // list with a numeric-ID "equals" filter matches what's on screen.
    mapFilterItems: (items) => mapNumericFilterItemsForWire(items, SHIPMENT_NUMERIC_FILTER_FIELDS),
    rowCount: total,
    pageSize,
    sorting: sortModel.sort ? [{ field: sortModel.field, sort: sortModel.sort }] : [],
    // No multiSearchQuery: this list has no free-text search — neither
    // useShipmentListData nor the shipments API exposes one.
  });

  const getRowDetailHref = useCallback(
    (row: Record<string, unknown>) =>
      detailPathFor(Number(row.id), String(row.shipmentTypeCode ?? '')),
    []
  );

  const getRowActions = useCallback(
    (row: Record<string, unknown>): readonly StructureRowAction[] => {
      const actions: StructureRowAction[] = [{ key: 'print_label', label: 'Print Label' }];
      const connoteNo = row.connoteNo;
      if (typeof connoteNo === 'string' && connoteNo.trim() !== '') {
        actions.push({ key: 'print_connote', label: 'Print Connote' });
        if (row.shipmentTypeCode === ShipmentTypes.Dispatch) {
          actions.push({ key: 'print_return_connote', label: 'Print Return Connote' });
        }
      }
      if (canReceiveRow(row, permissions)) {
        actions.push({ key: 'receive', label: 'Receive' });
      }
      return actions;
    },
    [permissions]
  );

  const handleRowAction = useCallback(
    async (id: number, action: string) => {
      if (!id) return;
      // Navigation, not a network call — kept out of the try/catch below so a
      // routing failure here can't surface the print-specific error message.
      if (action === 'receive') {
        navigate(`/shipments/receive/${id}`);
        return;
      }
      try {
        if (action === 'print_label') {
          const labelText = await getPrintLabel(PrintLabelEntityTypes.Shipment, id);
          await printLabel(labelText, PrintLabelEntityTypes.Shipment);
          return;
        }

        if (action === 'print_connote' || action === 'print_return_connote') {
          const orders = await freightOrderByShipment(id);
          const isReturn = action === 'print_return_connote';
          const order = orders.find((o) => (isReturn ? o.isReturnOrder : !o.isReturnOrder));
          if (!order) {
            toast.info(
              isReturn
                ? 'No return freight order found for this shipment.'
                : 'No freight order found for this shipment.'
            );
            return;
          }
          const labelText = await getPrintLabel(PrintLabelEntityTypes.FreightOrderLabel, order.id);
          await printLabel(labelText, PrintLabelEntityTypes.FreightOrderLabel);
        }
      } catch {
        toast.error('Failed to fetch label. Please try again.');
      }
    },
    [navigate]
  );

  const hasSearchPermission = isUserHasPermission(
    ActionPermissions.Shipment_View_Search,
    permissions
  );

  const canViewStructure = isUserHasPermission(
    ActionPermissions.Structure_Edit_View_Structure_Location,
    permissions
  );

  const tableData = useMemo(() => data ?? [], [data]);
  const tableSortModel = useMemo<SortModel>(
    () => ({
      field: sortModel.field,
      direction: (sortModel.sort as 'asc' | 'desc') ?? 'desc',
    }),
    [sortModel.field, sortModel.sort]
  );

  return (
    <>
      <ShipmentListHeader
        total={total}
        permissions={permissions}
        exportAnchor={exportAnchor}
        onExportClick={(e) => setExportAnchor(e.currentTarget)}
        onExportClose={() => setExportAnchor(null)}
        onExportCsv={() => handleExport('csv')}
        onExportExcel={() => handleExport('xlsx')}
        onColumnsClick={() => setColumnSelectionOpen(true)}
      />
      <PageContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {hasSearchPermission ? (
            <>
              <FilterBar
                onSearch={handleSearch}
                statuses={[]}
                jobTypes={[]}
                contracts={[]}
                quickFilters={quickFilters}
                search={{ placement: 'none', buttonAriaLabel: 'Search shipments' }}
                clearFilters={EMPTY_FILTERS}
                fieldConfig={fieldConfig}
                initialFilters={filters}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '20px',
                  py: '3px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    fontSize: '12px',
                    color: 'text.secondary',
                  }}
                >
                  {total > 0 && (
                    <span>
                      Showing {page * pageSize + 1}&ndash;{Math.min((page + 1) * pageSize, total)}{' '}
                      of {total.toLocaleString()}
                    </span>
                  )}
                  {fetchDurationMs !== null && (
                    <span>Took {(fetchDurationMs / 1000).toFixed(2)}s</span>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '12px',
                    color: 'text.secondary',
                  }}
                >
                  <label htmlFor="shipments-rows-per-page-select">Rows:</label>
                  <Select
                    id="shipments-rows-per-page-select"
                    size="small"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(0);
                    }}
                    aria-label="Rows per page"
                    sx={{
                      fontSize: '12px',
                      height: 26,
                      '& .MuiSelect-select': { py: 0.25, px: 1 },
                    }}
                  >
                    {[25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size} sx={{ fontSize: '12px' }}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
              <ShipmentTable
                data={tableData}
                columns={columns}
                sortModel={tableSortModel}
                onSortChange={handleSortChange}
                defaultSortField="id"
                getDetailHref={getRowDetailHref}
                getRowActions={getRowActions}
                onRowAction={handleRowAction}
                onColumnReorder={reorderColumns}
                loading={isFetching}
                canViewStructure={canViewStructure}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '20px',
                  pb: 2,
                }}
              >
                <Box sx={{ width: 160 }} />
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
                <AutoRefresh interval={autoRefreshInterval} onChange={setAutoRefreshInterval} />
              </Box>
            </>
          ) : (
            <Typography sx={{ mt: 4, px: '20px', color: theme.palette.text.secondary }}>
              You do not have permission to view shipments.
            </Typography>
          )}

          <ColumnSelection
            open={columnSelectionOpen}
            title="Select Columns"
            value={columnSelectionData}
            onChange={updateColumnSelection}
            onClose={() => setColumnSelectionOpen(false)}
          />
        </Box>
      </PageContainer>
    </>
  );
};

export const ShipmentList: React.FC = () => {
  const location = useLocation();
  return <ShipmentListInner key={location.pathname} />;
};
